import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Drawer, drawerClasses, IconButton, Stack, styled, Typography } from "~/common/mui";
import { Close } from "@mui/icons-material";

import { Chip } from "~/common/mui/Chip/Chip";
import { STICKY_TO_BOTTOM } from "~/common/components/icon/Elevation";
import { verticalStacker } from "~/styles/styledMixins";
import {
  Filter,
  type FacetFilterData,
  type FilterGroupData,
  type TechValueFilterCategoryData,
} from "~/features/search/models/filterConfigModels";
import { useTranslation } from "react-i18next";
import { selectAppliedTechFilterValues, selectBreadcrumbs } from "~/features/search/redux/searchSelectors";
import { useDispatch, useSelector } from "react-redux";
import { BreadcrumbType } from "~/utils/models";
import { applyAllFiltersAction, clearFacetFilters } from "~/features/search/redux/actions";
import { useIsMinTabletSize } from "~/common/hooks/useMediaQuery";
import CloseIcon from "@mui/icons-material/Close";
import { InnerTechFilterCategory } from "~/features/search/components/Filters/components/TechFilter/TechFilterCategory";
import { selectVisibleFilters } from "~/features/search/redux/filterConfigSelectors";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import {
  flattenTechFilterFormData,
  unflattenTechFilterFormData,
} from "~/features/search/utils/flattenTechFilterFormData";
import { DialogActions, DialogContent } from "~/common/mui/Dialog/Dialog";
import { InnerFacetFilter } from "~/features/search/components/Filters/components/FacetFilter/FacetFilter";

type DrawerStrategy = {
  label: string;
  breadcrumbLength: number;
  isClearEnabled: boolean;
  chipDelete?: () => void;
  renderContent: () => JSX.Element | null;
  apply: () => void;
  isApplyDisabled: boolean;
  onClearClick?: () => void;
  testIdSuffix: string | number;
};

export const CategoryChip = ({ index }: { index: number }) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const isMinTablet = useIsMinTabletSize();
  const { t } = useTranslation();

  const handleClose = () => {
    setIsDrawerOpen(false);
  };

  const dispatch = useDispatch();

  const visibleFilters = useSelector(selectVisibleFilters);
  const filterGroup = useMemo<FilterGroupData | undefined>(() => {
    const filtered = visibleFilters.filter((item) => item.type === Filter.TECH_VALUE || item.type === Filter.FACET);

    return filtered[index];
  }, [visibleFilters, index]);

  const breadcrumbs = useSelector(selectBreadcrumbs);
  const appliedTechFilterValues = useSelector(selectAppliedTechFilterValues);

  const methods = useForm({
    values: unflattenTechFilterFormData(appliedTechFilterValues),
    mode: "onChange",
  });

  useEffect(() => {
    if (isDrawerOpen) {
      methods.reset();
    }
  }, [appliedTechFilterValues, methods, isDrawerOpen]);

  const allValues = useWatch({ control: methods.control });

  const onSubmit = useCallback(
    (data: Record<string, { min?: number | undefined; max?: number | undefined }>) => {
      const flattenedData = flattenTechFilterFormData(data);
      dispatch(applyAllFiltersAction(flattenedData));
      setIsDrawerOpen(false);
    },
    [dispatch],
  );

  const resetFilters = useCallback(
    (filterUids?: string[]) => {
      const currentValues = methods.getValues();

      const uidsToReset = filterUids || Object.keys(currentValues);

      uidsToReset.forEach((uid) => {
        methods.setValue(`${uid}.min`, undefined, { shouldDirty: true, shouldTouch: true });
        methods.setValue(`${uid}.max`, undefined, { shouldDirty: true, shouldTouch: true });
      });

      methods.handleSubmit(onSubmit)();
    },
    [methods, onSubmit],
  );

  const buildTechStrategy = useCallback(
    (techCategory: TechValueFilterCategoryData): DrawerStrategy => {
      const applyHandler = methods.handleSubmit(onSubmit);
      const validTechValueCodes = techCategory.items.flatMap((item) => item.uid);
      const breadcrumbLength = breadcrumbs.filter(
        (breadcrumb) => breadcrumb.type === BreadcrumbType.TECH_VALUE && validTechValueCodes.includes(breadcrumb.code),
      ).length;

      const baseLabel = t(techCategory.title);
      const label = breadcrumbLength > 0 ? `${baseLabel} (${breadcrumbLength})` : baseLabel;

      const isClearEnabled =
        techCategory.items.some(
          (item) => allValues[item.uid]?.["min"] !== undefined || allValues[item.uid]?.["max"] !== undefined,
        ) ?? false;

      const onClearClick = () => {
        const filterUids = techCategory.items.map((item) => item.uid);
        resetFilters(filterUids);
        setIsDrawerOpen(false);
      };

      return {
        label,
        breadcrumbLength,
        isClearEnabled,
        chipDelete: breadcrumbLength > 0 ? onClearClick : undefined,
        renderContent: () => (
          <FormProvider {...methods}>
            <form onSubmit={applyHandler}>
              <Stack gap={4}>
                <InnerTechFilterCategory data={techCategory} />
              </Stack>
              <button hidden type={"submit"} />
            </form>
          </FormProvider>
        ),
        apply: applyHandler,
        isApplyDisabled: !methods.formState.isDirty,
        onClearClick,
        testIdSuffix: techCategory.facetId ?? Filter.TECH_VALUE,
      };
    },
    [allValues, breadcrumbs, methods, onSubmit, resetFilters, t],
  );

  const buildFacetStrategy = useCallback(
    (facetGroup: FacetFilterData): DrawerStrategy => {
      const breadcrumbLength = breadcrumbs.filter(
        (breadcrumb) => breadcrumb.type === BreadcrumbType.FACET && breadcrumb.facetCode === facetGroup.code,
      ).length;
      const baseLabel = t(facetGroup.title);
      const label = breadcrumbLength > 0 ? `${baseLabel} (${breadcrumbLength})` : baseLabel;

      const onClearClick = () => {
        dispatch(clearFacetFilters(facetGroup.code));
        setIsDrawerOpen(false);
      };

      return {
        label,
        breadcrumbLength,
        isClearEnabled: breadcrumbLength > 0,
        chipDelete: undefined,
        renderContent: () => <InnerFacetFilter data={facetGroup} />,
        apply: () => setIsDrawerOpen(false),
        isApplyDisabled: false,
        onClearClick,
        testIdSuffix: Filter.FACET,
      };
    },
    [breadcrumbs, dispatch, t],
  );

  const strategy = useMemo<DrawerStrategy | null>(() => {
    if (!filterGroup) {
      return null;
    }

    const builders: Partial<Record<(typeof Filter)[keyof typeof Filter], (group: FilterGroupData) => DrawerStrategy>> =
      {
        [Filter.TECH_VALUE]: (group) => buildTechStrategy(group as TechValueFilterCategoryData),
        [Filter.FACET]: (group) => buildFacetStrategy(group as FacetFilterData),
      };

    const builder = builders[filterGroup.type];
    return builder ? builder(filterGroup) : null;
  }, [buildFacetStrategy, buildTechStrategy, filterGroup]);

  if (!strategy) {
    return null;
  }

  return (
    <>
      <Chip
        variant={"filter"}
        onClick={() => setIsDrawerOpen(true)}
        label={strategy.label}
        selected={strategy.breadcrumbLength > 0}
        onDelete={strategy.chipDelete}
        deleteIcon={<CloseIcon />}
      />

      <StyledDrawer anchor={isMinTablet ? "left" : "bottom"} open={isDrawerOpen} onClose={handleClose}>
        <DrawerContent sx={{ ...verticalStacker("24px") }}>
          <DrawerHeader>
            <Typography variant="titleLarge" sx={{ fontWeight: "bold" }}>
              {strategy.label}
            </Typography>
            <IconButton onClick={handleClose} color={"secondary"} variant={"contained"}>
              <Close />
            </IconButton>
          </DrawerHeader>
          {strategy.renderContent()}
        </DrawerContent>

        <DrawerActions>
          <Button
            variant={"text"}
            fullWidth
            disabled={!strategy.isClearEnabled}
            onClick={strategy.onClearClick}
            data-test-id={`tech-filter-category-${strategy.testIdSuffix}-reset`}
          >
            {t("searchPage.refinement.button.clear")}
          </Button>
          <Button
            disabled={strategy.isApplyDisabled}
            fullWidth
            data-test-id={`tech-filter-category-${strategy.testIdSuffix}-apply`}
            onClick={strategy.apply}
          >
            {t("searchPage.refinement.button.apply")}
          </Button>
        </DrawerActions>
      </StyledDrawer>
    </>
  );
};

const StyledDrawer = styled(Drawer)(({ theme }) => ({
  [`.${drawerClasses.paper}`]: {
    [theme.breakpoints.down("tablet")]: {
      height: "70vh",
      maxHeight: "700px",
    },
    [theme.breakpoints.between("tablet", "desktop-s")]: {
      width: "45vw",
      maxWidth: "550px",
    },
  },
}));

const DrawerHeader = styled("div")({
  width: "100%",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
});

const DrawerContent = styled(DialogContent)({
  padding: "16px",
});

const DrawerActions = styled(DialogActions)({
  padding: "8px 16px",
  boxShadow: STICKY_TO_BOTTOM,
});
